import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Loader2, Plus, Package, Pencil, Trash2, ImagePlus, Tag, Clock,
  DollarSign, Percent, CheckCircle2, XCircle, Scissors,
} from "lucide-react";

type PackageItem = {
  packageId: number;
  serviceId: number;
  sortOrder: number;
  serviceName: string;
  serviceDuration: number;
  servicePrice: string;
  serviceCategory: string | null;
  serviceImageUrl: string | null;
};

type ServicePackage = {
  id: number;
  salonId: number;
  name: string;
  description: string | null;
  imageUrl: string | null;
  discountType: string;
  discountValue: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  items: PackageItem[];
};

const emptyForm = {
  name: "",
  description: "",
  discountType: "fixed" as "fixed" | "percent",
  discountValue: "0.00",
  isActive: true,
  serviceIds: [] as number[],
};

function calcPackagePrice(items: PackageItem[], discountType: string, discountValue: string) {
  const total = items.reduce((sum, i) => sum + parseFloat(i.servicePrice ?? "0"), 0);
  const disc = parseFloat(discountValue ?? "0");
  if (discountType === "percent") {
    return Math.max(0, total - (total * disc) / 100);
  }
  return Math.max(0, total - disc);
}

function calcTotalDuration(items: PackageItem[]) {
  return items.reduce((sum, i) => sum + (i.serviceDuration ?? 0), 0);
}

export default function AdminPackages() {
  const utils = trpc.useUtils();
  const { data: packages = [], isLoading } = (trpc as any).packages.list.useQuery();
  const { data: services = [] } = (trpc as any).services.list.useQuery();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<ServicePackage | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const createMutation = (trpc as any).packages.create.useMutation({
    onSuccess: () => { utils.packages.list.invalidate(); toast.success("Pacote criado com sucesso!"); },
    onError: (e: any) => toast.error(e.message ?? "Erro ao criar pacote"),
  });
  const updateMutation = (trpc as any).packages.update.useMutation({
    onSuccess: () => { utils.packages.list.invalidate(); toast.success("Pacote atualizado!"); },
    onError: (e: any) => toast.error(e.message ?? "Erro ao atualizar pacote"),
  });
  const deleteMutation = (trpc as any).packages.delete.useMutation({
    onSuccess: () => { utils.packages.list.invalidate(); toast.success("Pacote excluído!"); },
    onError: (e: any) => toast.error(e.message ?? "Erro ao excluir pacote"),
  });
  const uploadImageMutation = (trpc as any).packages.uploadImage.useMutation({
    onSuccess: () => utils.packages.list.invalidate(),
    onError: (e: any) => toast.error(e.message ?? "Erro ao enviar imagem"),
  });

  function openCreate() {
    setEditingPkg(null);
    setForm({ ...emptyForm });
    setImagePreview(null);
    setImageFile(null);
    setModalOpen(true);
  }

  function openEdit(pkg: ServicePackage) {
    setEditingPkg(pkg);
    setForm({
      name: pkg.name,
      description: pkg.description ?? "",
      discountType: (pkg.discountType as "fixed" | "percent") ?? "fixed",
      discountValue: pkg.discountValue ?? "0.00",
      isActive: pkg.isActive,
      serviceIds: pkg.items.map((i) => i.serviceId),
    });
    setImagePreview(pkg.imageUrl ?? null);
    setImageFile(null);
    setModalOpen(true);
  }

  function toggleService(serviceId: number) {
    setForm((f) => ({
      ...f,
      serviceIds: f.serviceIds.includes(serviceId)
        ? f.serviceIds.filter((id) => id !== serviceId)
        : [...f.serviceIds, serviceId],
    }));
  }

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Imagem muito grande. Máximo 2 MB."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (!form.name.trim()) { toast.error("Nome do pacote é obrigatório."); return; }
    if (form.serviceIds.length === 0) { toast.error("Selecione ao menos 1 serviço."); return; }
    setSaving(true);
    try {
      let pkgId: number;
      if (editingPkg) {
        await updateMutation.mutateAsync({ id: editingPkg.id, ...form });
        pkgId = editingPkg.id;
      } else {
        const res = await createMutation.mutateAsync(form);
        pkgId = res.id;
      }
      // Upload de imagem se houver arquivo novo
      if (imageFile && pkgId) {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve) => {
          reader.onload = (ev) => resolve(ev.target?.result as string);
          reader.readAsDataURL(imageFile);
        });
        await uploadImageMutation.mutateAsync({ packageId: pkgId, base64, mimeType: imageFile.type });
      }
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    await deleteMutation.mutateAsync({ id: deleteId });
    setDeleteId(null);
  }

  // Calcular preço original e com desconto para preview no formulário
  const selectedServices = (services as any[]).filter((s: any) => form.serviceIds.includes(s.id));
  const previewItems = selectedServices.map((s: any) => ({
    packageId: 0, serviceId: s.id, sortOrder: 0,
    serviceName: s.name, serviceDuration: s.durationMinutes,
    servicePrice: s.price, serviceCategory: s.category, serviceImageUrl: s.imageUrl,
  }));
  const originalTotal = previewItems.reduce((sum, i) => sum + parseFloat(i.servicePrice ?? "0"), 0);
  const discountedTotal = calcPackagePrice(previewItems, form.discountType, form.discountValue);
  const totalDuration = calcTotalDuration(previewItems);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pacotes de Serviços</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Crie combos pré-definidos com desconto que aparecem como opção especial no agendamento online.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="w-4 h-4" />
          Novo Pacote
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (packages as ServicePackage[]).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-lg">Nenhum pacote criado</p>
              <p className="text-muted-foreground text-sm mt-1">
                Crie combos como "Pacote Noiva" ou "Pacote Bronze Completo" para atrair mais clientes.
              </p>
            </div>
            <Button onClick={openCreate} className="gap-2">
              <Plus className="w-4 h-4" />
              Criar primeiro pacote
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(packages as ServicePackage[]).map((pkg) => {
            const origTotal = pkg.items.reduce((s, i) => s + parseFloat(i.servicePrice ?? "0"), 0);
            const finalPrice = calcPackagePrice(pkg.items, pkg.discountType, pkg.discountValue);
            const duration = calcTotalDuration(pkg.items);
            const saved = origTotal - finalPrice;
            return (
              <Card key={pkg.id} className={`relative overflow-hidden transition-all ${!pkg.isActive ? "opacity-60" : ""}`}>
                {pkg.imageUrl && (
                  <div className="h-32 w-full overflow-hidden">
                    <img src={pkg.imageUrl} alt={pkg.name} className="w-full h-full object-cover" />
                  </div>
                )}
                {!pkg.imageUrl && (
                  <div className="h-24 w-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Package className="w-10 h-10 text-primary/40" />
                  </div>
                )}
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-base leading-tight">{pkg.name}</h3>
                      {pkg.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{pkg.description}</p>
                      )}
                    </div>
                    <Badge variant={pkg.isActive ? "default" : "secondary"} className="shrink-0 text-xs">
                      {pkg.isActive ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>

                  {/* Serviços incluídos */}
                  <div className="flex flex-wrap gap-1">
                    {pkg.items.map((item) => (
                      <span key={item.serviceId} className="inline-flex items-center gap-1 text-xs bg-muted rounded-full px-2 py-0.5">
                        <Scissors className="w-3 h-3 text-muted-foreground" />
                        {item.serviceName}
                      </span>
                    ))}
                  </div>

                  {/* Duração e preço */}
                  <div className="flex items-center justify-between pt-1 border-t border-border">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      {duration} min
                    </div>
                    <div className="text-right">
                      {saved > 0 && (
                        <p className="text-xs text-muted-foreground line-through">
                          R$ {origTotal.toFixed(2).replace(".", ",")}
                        </p>
                      )}
                      <p className="font-bold text-primary">
                        R$ {finalPrice.toFixed(2).replace(".", ",")}
                      </p>
                      {saved > 0 && (
                        <p className="text-xs text-green-600 font-medium">
                          Economia de R$ {saved.toFixed(2).replace(".", ",")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => openEdit(pkg)}>
                      <Pencil className="w-3.5 h-3.5" />
                      Editar
                    </Button>
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteId(pkg.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de criação/edição */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPkg ? "Editar Pacote" : "Novo Pacote"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Imagem de capa */}
            <div className="space-y-2">
              <Label>Imagem de capa (opcional)</Label>
              <div
                className="relative w-full h-36 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors overflow-hidden bg-muted/30"
                onClick={() => imageInputRef.current?.click()}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <ImagePlus className="w-8 h-8" />
                    <span className="text-sm">Clique para adicionar imagem</span>
                    <span className="text-xs">JPG, PNG ou WebP · máx. 2 MB</span>
                  </div>
                )}
              </div>
              <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageChange} />
              {imagePreview && (
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => { setImagePreview(null); setImageFile(null); }}>
                  Remover imagem
                </Button>
              )}
            </div>

            {/* Nome e descrição */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pkg-name">Nome do pacote *</Label>
                <Input
                  id="pkg-name"
                  placeholder="ex: Pacote Noiva, Bronze Completo..."
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pkg-desc">Descrição</Label>
                <Textarea
                  id="pkg-desc"
                  placeholder="Descreva o que está incluído no pacote..."
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Desconto */}
            <div className="space-y-2">
              <Label>Desconto</Label>
              <div className="flex gap-3 items-end">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Select
                    value={form.discountType}
                    onValueChange={(v) => setForm((f) => ({ ...f, discountType: v as "fixed" | "percent" }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">
                        <span className="flex items-center gap-2"><DollarSign className="w-3.5 h-3.5" /> Valor fixo (R$)</span>
                      </SelectItem>
                      <SelectItem value="percent">
                        <span className="flex items-center gap-2"><Percent className="w-3.5 h-3.5" /> Percentual (%)</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    {form.discountType === "percent" ? "Desconto (%)" : "Desconto (R$)"}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={form.discountValue}
                    onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Serviços */}
            <div className="space-y-2">
              <Label>Serviços incluídos *</Label>
              <div className="border rounded-xl divide-y max-h-56 overflow-y-auto">
                {(services as any[]).filter((s: any) => s.isActive).map((service: any) => (
                  <label key={service.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                    <Checkbox
                      checked={form.serviceIds.includes(service.id)}
                      onCheckedChange={() => toggleService(service.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{service.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {service.durationMinutes} min · R$ {parseFloat(service.price).toFixed(2).replace(".", ",")}
                        {service.category && ` · ${service.category}`}
                      </p>
                    </div>
                    {form.serviceIds.includes(service.id) && (
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    )}
                  </label>
                ))}
                {(services as any[]).filter((s: any) => s.isActive).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">Nenhum serviço ativo cadastrado.</p>
                )}
              </div>
            </div>

            {/* Preview do preço */}
            {form.serviceIds.length > 0 && (
              <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 space-y-2">
                <p className="text-sm font-semibold text-primary flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Resumo do pacote
                </p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Duração total</p>
                    <p className="font-semibold">{totalDuration} min</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Preço original</p>
                    <p className="font-semibold line-through text-muted-foreground">R$ {originalTotal.toFixed(2).replace(".", ",")}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Preço do pacote</p>
                    <p className="font-bold text-primary text-lg">R$ {discountedTotal.toFixed(2).replace(".", ",")}</p>
                  </div>
                </div>
                {originalTotal > discountedTotal && (
                  <p className="text-xs text-green-600 font-medium text-center">
                    Cliente economiza R$ {(originalTotal - discountedTotal).toFixed(2).replace(".", ",")}
                  </p>
                )}
              </div>
            )}

            {/* Ativo */}
            <div className="flex items-center justify-between rounded-xl border p-4">
              <div>
                <p className="font-medium text-sm">Pacote ativo</p>
                <p className="text-xs text-muted-foreground">Pacotes ativos aparecem no agendamento online</p>
              </div>
              <Switch
                checked={form.isActive}
                onCheckedChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
              />
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingPkg ? "Salvar alterações" : "Criar pacote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir pacote?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O pacote será removido do agendamento online.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
